import React, { useState } from 'react';
import { View, ScrollView, RefreshControl } from 'react-native';
import { dynamicTranslations } from '@shared/utils/dynamicTranslation';
import { TimeFilterDropdown } from './filters/TimeFilterDropdown';
import { useFetch } from 'app/queryProvider';
import { Loading } from '@shared/components/loading';
import { useAuth } from 'app/auth/hooks/useAuth';
import MainErrorWithIcon from '@shared/components/ModularInfoWithIcon';
import { Head } from '@shared/components/head';
import ConfirmModal from '@shared/components/md-design-system/ConfirmModal/ConfirmModal';
import { useCancelTeleassistanceRequest } from '../hooks/useCancelTeleassistanceRequest';
import { TelevisitsResponse } from '../types/Visits';
import { VisitsPeriodSection } from './VisitsPeriodSection';

const buildQueryParams = (isFilterApplied: boolean, selectedTime: string[], t: (key: string) => string) => {
    if (!isFilterApplied || selectedTime.length === 0) return '';
    const timeValues = selectedTime.map(time => {
        if (time === t('history.visits.filters.past')) return '"past"';
        if (time === t('history.visits.filters.future')) return '"future"';
        return time;
    });
    return `?${new URLSearchParams({ time_period: timeValues.join(',') }).toString()}`;
};

const HistoryVisitsSection: React.FC = () => {
    const { t } = dynamicTranslations();
    const { meData } = useAuth();
    const [selectedTime, setSelectedTime] = useState<string[]>([]);
    const [isTimeFilterApplied, setIsTimeFilterApplied] = useState(false);

    const { showDeleteModal, isCancelling, requestCancel, confirmCancel, dismissCancel } =
        useCancelTeleassistanceRequest();

    const queryString = buildQueryParams(isTimeFilterApplied, selectedTime, t);

    const { data: appointmentsResponse, isLoading, isRefetching, refetch } = useFetch<TelevisitsResponse>({
        url: `/by-month${queryString}`,
        queryKey: [['appointments', 'by-month', queryString]],
        resource: { name: 'appointments' },
        options: { enabled: !!meData?.current_pathway.id },
    });

    if (isLoading || !meData?.current_pathway.id) {
        return <Loading />;
    }

    const pastGroups = appointmentsResponse?.past ?? [];
    const futureGroups = appointmentsResponse?.future ?? [];

    const showFuture = !isTimeFilterApplied || selectedTime.includes(t('history.visits.filters.future'));
    const showPast = !isTimeFilterApplied || selectedTime.includes(t('history.visits.filters.past'));
    const hasAnyData = futureGroups.length > 0 || pastGroups.length > 0;
    const hasFilteredContent =
        (showFuture && futureGroups.length > 0) ||
        (showPast && pastGroups.length > 0);

    if (!hasAnyData && !isTimeFilterApplied) {
        return (
            <ScrollView className="flex-1" refreshControl={<RefreshControl refreshing={isRefetching} onRefresh={refetch} />}>
                <MainErrorWithIcon errorType="no_visits" />
            </ScrollView>
        );
    }

    return (
        <ScrollView className="flex-1" refreshControl={<RefreshControl refreshing={isRefetching} onRefresh={refetch} />}>
            <View className="mb-6">
                <Head type="h2">{t('history.visits.title')}</Head>
            </View>

            <View className="mb-6 flex-row">
                <TimeFilterDropdown
                    selectedTime={selectedTime}
                    onTimeChange={setSelectedTime}
                    onApplyFilter={() => setIsTimeFilterApplied(true)}
                    onClearFilter={() => { setSelectedTime([]); setIsTimeFilterApplied(false); }}
                    isFilterApplied={isTimeFilterApplied}
                />
            </View>

            {!hasFilteredContent && (
                <MainErrorWithIcon errorType={isTimeFilterApplied ? 'no_visits_filtered' : 'no_visits'} />
            )}

            {showFuture && futureGroups.length > 0 && (
                <VisitsPeriodSection
                    title={t('history.visits.sections.future')}
                    monthGroups={futureGroups}
                    onRequestCancel={requestCancel}
                    isCancelling={isCancelling}
                />
            )}

            {showPast && pastGroups.length > 0 && (
                <VisitsPeriodSection
                    title={t('history.visits.sections.past')}
                    monthGroups={pastGroups}
                    onRequestCancel={requestCancel}
                    isCancelling={isCancelling}
                />
            )}

            <ConfirmModal
                visible={showDeleteModal}
                title={t('teleassistance.card.deleteModal.title')}
                message={t('teleassistance.card.deleteModal.message')}
                confirmLabel={t('teleassistance.card.deleteModal.confirm')}
                cancelLabel={t('teleassistance.card.deleteModal.cancel')}
                onConfirm={confirmCancel}
                onCancel={dismissCancel}
                isLoading={isCancelling}
                loadingLabel={t('teleassistance.card.deleteModal.deleting')}
                showWarningIcon
            />
        </ScrollView>
    );
};

export default HistoryVisitsSection;
